// FirstRunChecklist — Phase 10 / E11. Compact onboarding card driven by an
// items[] prop so callers can wire whatever signals make sense (profile
// complete, first resume, first job saved, etc.). Hides itself when all
// items are done unless `alwaysShow` is set.
//
// Pass `onStepComplete` to persist step completion to the backend.

import React from 'react';
import './FirstRunChecklist.css';

export interface FirstRunItem {
  id: string;
  label: string;
  done: boolean;
  ctaLabel?: string;
  onCta?: () => void;
}

export interface FirstRunChecklistProps {
  items: FirstRunItem[];
  title?: string;
  alwaysShow?: boolean;
  storageKey?: string;
  onStepComplete?: (stepId: string) => void; // retained for API compat; no longer used
}

const FirstRunChecklist: React.FC<FirstRunChecklistProps> = ({
  items,
  title = 'Get started',
  alwaysShow = false,
  storageKey,
}) => {
  const dismissed = storageKey ? typeof window !== 'undefined' && window.localStorage.getItem(storageKey) === 'true' : false;
  const total = items.length;
  const doneCount = items.filter((i) => i.done).length;
  const allDone = total > 0 && doneCount === total;

  if (dismissed) return null;
  if (allDone && !alwaysShow) return null;

  const percent = total === 0 ? 0 : Math.round((doneCount / total) * 100);

  const handleCta = (item: FirstRunItem) => {
    // Only navigate — DO NOT mark the step complete here. Clicking "Connect" /
    // "Upload" means the user is *about* to do the task, not that it succeeded.
    // Completion is derived from real state by the caller (e.g. an actually
    // connected email account), so the checklist can never show a false ✓.
    if (item.onCta) item.onCta();
  };

  return (
    <section className="first-run-card" aria-label={title}>
      <header className="first-run-header">
        <h3 className="first-run-title">{title}</h3>
        <span className="first-run-progress">{doneCount}/{total} complete</span>
      </header>
      <div className="first-run-progress-bar" aria-hidden="true">
        <div className="first-run-progress-fill" style={{ width: `${percent}%` }} />
      </div>
      <ul className="first-run-list">
        {items.map((item) => (
          <li key={item.id} className={`first-run-item ${item.done ? 'done' : ''}`}>
            <span className="first-run-check" aria-hidden="true">{item.done ? '✓' : ''}</span>
            <span>{item.label}</span>
            {!item.done && item.ctaLabel && item.onCta && (
              <button type="button" className="first-run-action" onClick={() => handleCta(item)}>
                {item.ctaLabel} →
              </button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
};

export default FirstRunChecklist;
